import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { useToast } from '@/hooks/use-toast';
import { getUserFriendlyError } from '@/lib/constants/errorMessages';

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget: number | null;
  address: string | null;
  lead_id: string | null;
  division_id: string | null;
  created_at: string;
  updated_at: string;
  divisions?: {
    id: string;
    name: string;
  } | null;
}

export interface ProjectFormData {
  name: string;
  description?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  budget?: number;
  address?: string;
  lead_id?: string;
  division_id?: string;
}

export function useProjects() {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', tenantId],
    queryFn: async () => {
      if (!user || !tenantId) return [];

      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          divisions (id, name)
        `)
        .eq('tenant_id', tenantId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Project[];
    },
    enabled: !!user && !!tenantId,
  });

  const createProject = useMutation({
    mutationFn: async (projectData: ProjectFormData) => {
      if (!user) throw new Error('User not authenticated');
      if (!tenantId) throw new Error('Tenant not found');

      const { data, error } = await supabase
        .from('projects')
        .insert([{ 
          ...projectData, 
          user_id: user.id,
          tenant_id: tenantId,
        }])
        .select('*, divisions (id, name)')
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: "Success",
        description: "Project created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating project:', error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  const updateProject = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ProjectFormData> }) => {
      if (!user) throw new Error('User not authenticated');

      const { data: updated, error } = await supabase
        .from('projects')
        .update(data)
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId)
        .select('*, divisions (id, name)')
        .single();

      if (error) throw error;
      return updated as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: "Success",
        description: "Project updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating project:', error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  const deleteProject = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: "Success",
        description: "Project deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  return {
    projects,
    isLoading,
    createProject: createProject.mutateAsync,
    updateProject: ({ id, data }: { id: string; data: Partial<ProjectFormData> }) => 
      updateProject.mutateAsync({ id, data }),
    deleteProject: deleteProject.mutateAsync,
  };
}
